import type { PoiItem } from '@/types/map';
import type { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import type { IPoiOption } from 'dabeeomaps/dist/src/model/map/IPoiOption';
import { useCallback, useMemo } from 'react';

export const usePoi = (currentMap?: DabeeoMap, mapData?: DabeeoMapData) => {
    const setPoi = useCallback(
        (options: IPoiOption) => {
            if (!currentMap) return;
            currentMap.pois.set(options);
        },
        [currentMap]
    );

    const resetPoi = useCallback(
        (id?: string | string[]) => {
            if (!currentMap) return;
            currentMap.pois.reset(id);
        },
        [currentMap]
    );

    const mapPoiList = useMemo(() => {
        const pois: PoiItem[] = mapData?.dataPoi?.getPois() as PoiItem[];
        if (!Array.isArray(pois) || !pois?.length) {
            return [];
        }

        return pois;
    }, [mapData?.dataPoi]);

    const convertFloorsToKoreanMap = useMemo(() => {
        const floors = mapData?.dataFloor?.getFloors() || [];
        return floors.reduce<{ [key: string]: string | null }>((acc, floor) => {
            const korean = floor.name.find((n) => n.lang === 'ko');
            acc[floor.id] = korean?.text ?? null;
            return acc;
        }, {});
    }, [mapData?.dataFloor]);

    const groupedPoiByFloorId = useMemo(() => {
        if (!Array.isArray(mapPoiList) || mapPoiList.length === 0) {
            return new Map<string, Map<string, PoiItem[]>>();
        }

        const poiMap = new Map<string, Map<string, PoiItem[]>>();
        for (const poi of mapPoiList) {
            const poiFloorName = convertFloorsToKoreanMap[poi.floorId] || '';
            let categoryMap = poiMap.get(poiFloorName);
            if (!categoryMap) {
                categoryMap = new Map<string, PoiItem[]>();
                poiMap.set(poiFloorName, categoryMap);
            }

            if (!poi?.categoryCode) {
                continue;
            }

            const categories = Array.isArray(poi.categoryCode) ? poi.categoryCode : [poi.categoryCode];

            for (const categoryCode of categories) {
                let poiList = categoryMap.get(categoryCode);
                if (!poiList) {
                    poiList = [];
                    categoryMap.set(categoryCode, poiList);
                }
                poiList.push(poi);
            }
        }
        return poiMap;
    }, [mapPoiList, convertFloorsToKoreanMap]);

    return {
        setPoi,
        resetPoi,
        mapPoiList,
        groupedPoiByFloorId,
    };
};
